import { createClient } from '@supabase/supabase-js';
import { getMasterSession, getStaffSession } from '../utils/session';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://jbrmvmcthslsilwhhseo.supabase.co';
// Default to the original anon key for out-of-the-box operation
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export const STORAGE_BUCKET = 'car-track-media';   // public assets (logos, photos)
export const SECURE_BUCKET = 'car-track-docs';     // private documents (CIN, permis, invoices…)

/**
 * Returns the bearer token to authenticate calls to our own backend
 * (e.g. /api/telegram/send): the Supabase access token for logged-in users, or
 * the signed master token for master-password logins. Empty string if neither.
 */
export async function getAuthToken() {
  // 1) Master-password login token (signed by the server)
  try {
    const mu = getMasterSession();
    if (mu && mu.token) return mu.token;
  } catch (_) {}

  // 1b) Employee (staff) session token
  try {
    const su = getStaffSession();
    if (su && su.token) return su.token;
  } catch (_) {}

  // 2) Supabase session access token
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session && session.access_token) return session.access_token;
  } catch (err) {
    console.warn('[AUTH] Failed to retrieve Supabase session token:', err?.message || err);
  }
  return '';
}

/**
 * Returns the correct MIME type for a given file extension.
 * This is critical for PDFs — if Supabase stores a PDF with image/jpeg,
 * browsers will refuse to render it inline.
 */
function getMimeType(ext) {
  const map = {
    pdf:  'application/pdf',
    jpg:  'image/jpeg',
    jpeg: 'image/jpeg',
    png:  'image/png',
    webp: 'image/webp',
    gif:  'image/gif',
    bmp:  'image/bmp',
    tiff: 'image/tiff',
    svg:  'image/svg+xml',
    heic: 'image/heic',
    heif: 'image/heif',
  };
  return map[(ext || '').toLowerCase()] || 'application/octet-stream';
}

/**
 * Helper to upload standard files to Supabase storage.
 * Sensitive documents (clients, staff_docs, maintenance_invoices) go to the private SECURE_BUCKET.
 * Public assets (cars, settings, staff_photos) go to the public STORAGE_BUCKET.
 */
// Strict upload allowlist — only documents/images, never executables or scripts.
const ALLOWED_UPLOAD_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
const ALLOWED_UPLOAD_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // 15 MB

export async function uploadToStorage(file, folder) {
  const agencyId = window.__AGENCY_ID__ || 'shared';
  const ext = (file.name || 'file').split('.').pop().toLowerCase();

  // ── Security: validate extension + MIME, reject everything else ──
  if (!ALLOWED_UPLOAD_EXT.includes(ext)) {
    throw new Error(`Type de fichier non autorisé (.${ext}). Autorisés : PDF, JPG, PNG, WEBP.`);
  }
  const declaredMime = (file.type || '').toLowerCase();
  if (declaredMime && !ALLOWED_UPLOAD_MIME.includes(declaredMime)) {
    throw new Error(`Type MIME non autorisé (${declaredMime}).`);
  }
  // Extension and MIME must agree (when the browser provides a MIME).
  const expectedMime = getMimeType(ext);
  if (declaredMime && expectedMime !== 'application/octet-stream' && declaredMime !== expectedMime) {
    throw new Error('Le type du fichier ne correspond pas à son extension.');
  }
  if (file.size && file.size > MAX_UPLOAD_BYTES) {
    throw new Error('Fichier trop volumineux (max 15 Mo).');
  }

  const filePath = `${agencyId}/${folder}/${Date.now()}.${ext}`;

  const isSensitive = ['clients', 'staff_docs', 'maintenance_invoices'].includes(folder);
  const bucketName = isSensitive ? SECURE_BUCKET : STORAGE_BUCKET;

  // Derive the correct MIME type from the extension so that PDFs are never
  // accidentally stored as image/jpeg (which breaks inline browser rendering).
  const correctMime = getMimeType(ext);
  const contentType = correctMime !== 'application/octet-stream'
    ? correctMime
    : (file.type || 'application/octet-stream');

  console.log(`[Storage] Uploading ${file.name} → ${filePath} (${contentType}) in bucket ${bucketName}`);

  const { error } = await supabase.storage
    .from(bucketName)
    .upload(filePath, file, {
      upsert: true,
      contentType,
    });

  if (error) throw new Error(error.message);

  if (isSensitive) {
    // Return a private URI that our secure DocumentViewerModal can resolve dynamically.
    return `private://${bucketName}/${filePath}`;
  }

  return supabase.storage.from(STORAGE_BUCKET).getPublicUrl(filePath).data.publicUrl;
}

/**
 * Helper to convert base64 string to a Blob manually without fetch()
 */
export function base64ToBlob(base64, mimeType = 'image/jpeg') {
  let base64Data = base64;
  if (base64.startsWith('data:')) {
    const parts = base64.split(',');
    base64Data = parts[1];
    const mimeMatch = parts[0].match(/data:(.*?);/);
    if (mimeMatch) {
      mimeType = mimeMatch[1];
    }
  }

  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
}

/**
 * Helper to upload base64 images or PDFs (e.g. from document scans or file pickers)
 */
export async function uploadBase64ToStorage(dataUrl, folder) {
  try {
    const blob = base64ToBlob(dataUrl);

    // Derive the correct extension from the real MIME type in the data URL.
    // Previously this always used 'scan.jpg' / 'image/jpeg', which broke PDFs.
    const mime = blob.type || 'image/jpeg';
    const mimeToExt = {
      'application/pdf': 'pdf',
      'image/jpeg':      'jpg',
      'image/png':       'png',
      'image/webp':      'webp',
      'image/gif':       'gif',
      'image/bmp':       'bmp',
      'image/tiff':      'tiff',
      'image/heic':      'heic',
      'image/heif':      'heif',
    };
    const ext = mimeToExt[mime] || 'jpg';
    const fileName = `scan.${ext}`;

    const fakeFile = new File([blob], fileName, { type: mime });
    return await uploadToStorage(fakeFile, folder);
  } catch (err) {
    console.error('Failed to convert base64 to Blob manually, attempting fallback fetch()', err);
    // Fallback if atob fails or anything else fails
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const mime = blob.type || 'image/jpeg';
    const ext = mime === 'application/pdf' ? 'pdf' : 'jpg';
    const fakeFile = new File([blob], `scan.${ext}`, { type: mime });
    return await uploadToStorage(fakeFile, folder);
  }
}

